import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Semester, SemesterRequest } from "../../models/semester";
import { Subject, SubjectRequest } from "../../models/subject";
import { addDocument } from "../../firebase/service";
import { RootState } from "../store";
import { getMasterMajor } from "../../master-data/masterData";
import { GradeFile } from "../../models/gradeFile";
import { db } from "../../firebase/config";
import { collection, doc, getDoc, updateDoc } from "firebase/firestore";
import subjectEquals from "../../utils/subjectCompare";
import { ExcelSemesterChange } from "../../features/FileDetail/ExcelImportModal/excelImportModal";
import {
  LOCAL_GRADE_FILE_KEY,
  LOCAL_GRADE_MAP_ID,
} from "../../constant/constant";
import sampleLocalFileJSON from "../../master-data/sampleLocalFile.json";
import sampleFileJSON from "../../master-data/sampleFile.json";

export interface SubjectFormData {
  visible: boolean;
  mode: "edit" | "create";
  subject: SubjectRequest | null | undefined;
  semesterId: number;
}

export interface SemesterFormData {
  visible: boolean;
  mode: "edit" | "create";
  semester: SemesterRequest | null | undefined;
}

interface GradeFileSlideState {
  gradeFile: GradeFile | null;
  type: "firestore" | "sample" | "local";
  gradeMapIdx: number;
  semesterState: Semester[];
  excelPopup: {
    visible: boolean;
    changeList: ExcelSemesterChange[];
  };
  subjectFormData: SubjectFormData;
  semesterFormData: SemesterFormData;
}

const getGradeMapIdxStored = () => {
  const gradeMapIdxStored = localStorage.getItem(LOCAL_GRADE_MAP_ID);
  if (!gradeMapIdxStored) return 0;
  const parsed = parseInt(gradeMapIdxStored);
  return isNaN(parsed) ? 0 : parsed;
};

const initialState: GradeFileSlideState = {
  gradeFile: null,
  type: "firestore",
  gradeMapIdx: getGradeMapIdxStored(),
  semesterState: [],
  excelPopup: {
    visible: false,
    changeList: [],
  },
  subjectFormData: {
    visible: false,
    mode: "edit",
    subject: {
      name: "",
      credit: 0,
    },
    semesterId: 0,
  },
  semesterFormData: {
    visible: false,
    mode: "edit",
    semester: {
      name: "",
    },
  },
};

const updateGradeFileSemester = async (
  gradeFileSliceState: GradeFileSlideState,
  newSemesterState: Semester[]
) => {
  if (gradeFileSliceState.type === "firestore") {
    await updateDoc(doc(collection(db, "files"), gradeFileSliceState.gradeFile?.id!), {
      semester: newSemesterState,
    });
  }
  if (gradeFileSliceState.type === "local") {
    localStorage.setItem(
      LOCAL_GRADE_FILE_KEY,
      JSON.stringify({
        ...gradeFileSliceState.gradeFile,
        semester: newSemesterState,
      })
    );
  }
};

export const fetchGradeFile = createAsyncThunk(
  "gradeFile/fetchGradeFile",
  async (id: string) => {
    const docSnap = await getDoc(doc(collection(db, "files"), id));
    if (docSnap.exists()) {
      let fileInfo = docSnap.data() as GradeFile;
      fileInfo.id = docSnap.id;
      fileInfo.createAt = JSON.parse(JSON.stringify(fileInfo.createAt));
      return fileInfo;
    } else {
      throw Error("Không tìm thấy hồ sơ");
    }
  }
);

export const saveGradeFile = createAsyncThunk(
  "gradeFile/saveGradeFile",
  async (arg, { getState }) => {
    const state = getState() as RootState;
    const newFileInfo: GradeFile = {
      ...state.gradeFile.gradeFile,
      semester: state.gradeFile.semesterState,
    } as GradeFile;
    if (state.gradeFile.type === "sample") {
      throw new Error("Cannot save sample file");
    }
    if (state.gradeFile.type === "firestore") {
      await updateDoc(doc(collection(db, "files"), state.gradeFile.gradeFile?.id!), {
        semester: state.gradeFile.semesterState,
        target: state.gradeFile.gradeFile?.target || null,
      });
    }
    if (state.gradeFile.type === "local") {
      localStorage.setItem(
        LOCAL_GRADE_FILE_KEY,
        JSON.stringify({
          ...state.gradeFile.gradeFile,
          semester: state.gradeFile.semesterState,
          target: state.gradeFile.gradeFile?.target || null,
        })
      );
    }
    return newFileInfo;
  }
);

export const saveGradeFileWithNewSemesterState = createAsyncThunk(
  "gradeFile/saveGradeFileWithNewSemesterState",
  async (newSemesterState: Semester[], { getState }) => {
    const state = getState() as RootState;
    const newFile = {
      ...state.gradeFile.gradeFile,
      semester: newSemesterState,
    } as GradeFile;
    await updateGradeFileSemester(state.gradeFile, newSemesterState);
    return newFile;
  }
);

export const duplicateGradeFile = createAsyncThunk(
  "gradeFile/duplicateGradeFile",
  async (arg, { getState }) => {
    const state = getState() as RootState;
    if (!state.auth.user) {
      throw Error("Cannot duplicate when missing user info");
    }
    const newFile: GradeFile = {
      ...state.gradeFile.gradeFile,
      semester: state.gradeFile.semesterState || [],
    } as GradeFile;
    delete newFile.id;
    delete newFile.createAt;
    newFile.uid = state.auth.user?.uid as string;
    newFile.fullName = `${newFile.fullName} (1)`;
    newFile.root = false;
    const docRef = await addDocument("files", newFile);
    newFile.id = docRef.id;
    return newFile;
  }
);

export const resetGradeFile = createAsyncThunk(
  "gradeFile/resetGradeFile",
  async (arg, { getState }) => {
    const state = getState() as RootState;
    if (state.gradeFile.type === "local") {
      const gradeFileJSON = JSON.stringify(sampleLocalFileJSON);
      localStorage.setItem(LOCAL_GRADE_FILE_KEY, gradeFileJSON);
      return JSON.parse(gradeFileJSON) as GradeFile;
    }
    if (!state.gradeFile.gradeFile?.course || !state.gradeFile.gradeFile?.major)
      throw Error("Course or Major not found");
    const newSemesterState = await getMasterMajor(
      state.gradeFile.gradeFile.course,
      state.gradeFile.gradeFile.major
    );
    const newFile: GradeFile = {
      ...state.gradeFile.gradeFile,
      target: "",
      semester: newSemesterState,
    };
    if (state.gradeFile.type === "firestore") {
      await updateDoc(doc(collection(db, "files"), state.gradeFile.gradeFile?.id!), {
        target: "",
        semester: newSemesterState,
      });
    }
    return newFile;
  }
);

export const updateSemesterStateSubject = createAsyncThunk(
  "gradeFile/updateSemesterStateSubject",
  async (
    {
      subject,
      semesterId,
    }: {
      subject: Subject;
      semesterId: number;
    },
    { getState }
  ) => {
    const state = getState() as RootState;
    const newSemesterState = state.gradeFile.semesterState.map((semester) => {
      if (semester.id === semesterId) {
        return {
          ...semester,
          subjects: semester.subjects.map((s) => {
            if (subjectEquals(s, subject)) {
              return {
                ...subject,
              };
            }
            return s;
          }),
        };
      }
      return semester;
    });
    const newFileInfo: GradeFile = {
      ...state.gradeFile.gradeFile,
      semester: newSemesterState,
    } as GradeFile;
    await updateGradeFileSemester(state.gradeFile, newSemesterState);
    return newFileInfo;
  }
);

export const deleteSemesterStateSubject = createAsyncThunk(
  "gradeFile/deleteSemesterStateSubject",
  async (
    {
      subject,
      semesterId,
    }: {
      subject: Subject;
      semesterId: number;
    },
    { getState }
  ) => {
    const state = getState() as RootState;
    const newSemesterState = state.gradeFile.semesterState.map((semester) => {
      if (semester.id === semesterId) {
        return {
          ...semester,
          subjects: semester.subjects.filter((s) => !subjectEquals(s, subject)),
        };
      }
      return semester;
    });
    const newFileInfo: GradeFile = {
      ...state.gradeFile.gradeFile,
      semester: newSemesterState,
    } as GradeFile;
    await updateGradeFileSemester(state.gradeFile, newSemesterState);
    return newFileInfo;
  }
);

export const addSemesterStateSubject = createAsyncThunk(
  "gradeFile/addSemesterStateSubject",
  async (
    {
      subject,
      semesterId,
    }: {
      subject: Subject;
      semesterId: number;
    },
    { getState }
  ) => {
    const state = getState() as RootState;
    const newSemesterState = state.gradeFile.semesterState.map((semester) => {
      if (semester.id === semesterId) {
        return {
          ...semester,
          subjects: [
            ...semester.subjects,
            {
              ...subject,
              id:
                semester.subjects.length === 0
                  ? 1
                  : semester.subjects[semester.subjects.length - 1].id + 1,
            },
          ],
        };
      }
      return semester;
    });
    const newFileInfo: GradeFile = {
      ...state.gradeFile.gradeFile,
      semester: newSemesterState,
    } as GradeFile;
    await updateGradeFileSemester(state.gradeFile, newSemesterState);
    return newFileInfo;
  }
);

export const updateSemesterStateSemester = createAsyncThunk(
  "gradeFile/updateSemesterStateSemester",
  async (semesterRequest: SemesterRequest, { getState }) => {
    const state = getState() as RootState;
    const newSemesterState = state.gradeFile.semesterState.map((semester) => {
      if (semester.id === semesterRequest.id) {
        return semesterRequest as Semester;
      }
      return semester;
    });
    const newFileInfo: GradeFile = {
      ...state.gradeFile.gradeFile,
      semester: newSemesterState,
    } as GradeFile;
    await updateGradeFileSemester(state.gradeFile, newSemesterState);
    return newFileInfo;
  }
);

export const deleteSemesterStateSemester = createAsyncThunk(
  "gradeFile/deleteSemesterStateSemester",
  async (semesterId: number, { getState }) => {
    const state = getState() as RootState;
    const newSemesterState = state.gradeFile.semesterState.filter(
      (semester) => semester.id !== semesterId
    );
    const newFileInfo: GradeFile = {
      ...state.gradeFile.gradeFile,
      semester: newSemesterState,
    } as GradeFile;
    await updateGradeFileSemester(state.gradeFile, newSemesterState);
    return newFileInfo;
  }
);

export const addSemesterStateSemester = createAsyncThunk(
  "gradeFile/addSemesterStateSemester",
  async (semesterRequest: SemesterRequest, { getState }) => {
    const state = getState() as RootState;
    const maxSemesterId =
      state.gradeFile.semesterState.length === 0
        ? 0
        : Math.max(
            ...state.gradeFile.semesterState.map((semester) => semester.id)
          );
    const newSemesterState: Semester[] = [
      ...state.gradeFile.semesterState,
      {
        id: maxSemesterId + 1,
        ...semesterRequest,
        subjects: [],
      },
    ];
    const newFileInfo: GradeFile = {
      ...state.gradeFile.gradeFile,
      semester: newSemesterState,
    } as GradeFile;
    await updateGradeFileSemester(state.gradeFile, newSemesterState);
    return newFileInfo;
  }
);

export const gradeFileSlide = createSlice({
  name: "gradeFile",
  initialState,
  reducers: {
    loadSampleGradeFile: (state) => {
      const sampleFileCloned = JSON.parse(JSON.stringify(sampleFileJSON));
      sampleFileCloned.semester = sampleFileCloned.semester.filter(
        (s: Semester) => s.subjects && s.subjects.length > 0
      );
      state.gradeFile = sampleFileCloned;
      state.semesterState = sampleFileCloned.semester || [];
      state.type = "sample";
      state.gradeMapIdx = 0;
    },
    loadLocalGradeFile: (state) => {
      const localFileStored = localStorage.getItem(LOCAL_GRADE_FILE_KEY);
      let localFile: GradeFile;
      if (localFileStored) {
        localFile = JSON.parse(localFileStored);
      } else {
        localFile = JSON.parse(JSON.stringify(sampleLocalFileJSON)) as any;
      }
      state.gradeFile = localFile;
      state.semesterState = localFile.semester || [];
      state.type = "local";
    },
    updateSemesterStateSubjectScreen: (
      state,
      action: PayloadAction<{
        subject: Subject;
        semesterId: number;
      }>
    ) => {
      const { semesterId, subject } = action.payload;
      state.semesterState = state.semesterState.map((semester) => {
        if (semester.id === semesterId) {
          return {
            ...semester,
            subjects: semester.subjects.map((s) => {
              if (subjectEquals(s, subject)) {
                return {
                  ...subject,
                };
              }
              return s;
            }),
          };
        }
        return semester;
      });
      if (state.type === "local") {
        localStorage.setItem(
          LOCAL_GRADE_FILE_KEY,
          JSON.stringify({
            ...state.gradeFile,
            semester: state.semesterState,
          })
        );
      }
    },
    setGradeTarget: (
      state,
      action: PayloadAction<{
        target: number | "";
        semesterState: Semester[];
      }>
    ) => {
      if (state.gradeFile) {
        if (!action.payload.target) {
          delete state.gradeFile.target;
        } else {
          state.gradeFile.target = action.payload.target;
        }
      }
      state.semesterState = action.payload.semesterState;
      if (state.type === "local") {
        localStorage.setItem(
          LOCAL_GRADE_FILE_KEY,
          JSON.stringify({
            ...state.gradeFile,
            semester: state.semesterState,
          })
        );
      }
    },
    openExcelPopup: (state, action: PayloadAction<ExcelSemesterChange[]>) => {
      state.excelPopup.changeList = action.payload;
      state.excelPopup.visible = true;
    },
    closeExcelPopup: (state) => {
      state.excelPopup.changeList = [];
      state.excelPopup.visible = false;
    },
    showSubjectFormData: (
      state,
      action: PayloadAction<{
        mode: "edit" | "create";
        subject: Subject | null | undefined;
        semesterId: number;
      }>
    ) => {
      state.subjectFormData = {
        ...action.payload,
        visible: true,
      };
    },
    hideSubjectFormData: (state) => {
      state.subjectFormData.visible = false;
    },
    showSemesterFormData: (
      state,
      action: PayloadAction<{
        mode: "edit" | "create";
        semester: SemesterRequest | null | undefined;
      }>
    ) => {
      state.semesterFormData = {
        ...action.payload,
        visible: true,
      };
    },
    hideSemesterFormData: (state) => {
      state.semesterFormData.visible = false;
    },
    setGradeMapIdx: (state, action: PayloadAction<string>) => {
      const parsed = parseInt(action.payload);
      if (!isNaN(parsed)) {
        state.gradeMapIdx = parsed;
        localStorage.setItem(LOCAL_GRADE_MAP_ID, action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchGradeFile.fulfilled,
      (state, action: PayloadAction<GradeFile>) => {
        state.gradeFile = action.payload;
        state.semesterState = action.payload.semester || [];
        state.type = "firestore";
      }
    );
    builder.addCase(saveGradeFile.fulfilled, (state, action) => {
      state.gradeFile = action.payload;
      state.semesterState = action.payload.semester || [];
    });
    builder.addCase(resetGradeFile.fulfilled, (state, action) => {
      state.gradeFile = action.payload;
      state.semesterState = action.payload.semester || [];
    });
    builder.addCase(
      saveGradeFileWithNewSemesterState.fulfilled,
      (state, action) => {
        state.semesterState = action.payload.semester || [];
        state.gradeFile = action.payload;
        state.excelPopup.visible = false;
        state.excelPopup.changeList = [];
      }
    );
    builder.addCase(updateSemesterStateSubject.fulfilled, (state, action) => {
      state.semesterState = action.payload.semester || [];
      state.gradeFile = action.payload;
      state.subjectFormData.visible = false;
    });
    builder.addCase(deleteSemesterStateSubject.fulfilled, (state, action) => {
      state.semesterState = action.payload.semester || [];
      state.gradeFile = action.payload;
      state.subjectFormData.visible = false;
    });
    builder.addCase(addSemesterStateSubject.fulfilled, (state, action) => {
      state.semesterState = action.payload.semester || [];
      state.gradeFile = action.payload;
      state.subjectFormData.visible = false;
    });
    builder.addCase(updateSemesterStateSemester.fulfilled, (state, action) => {
      state.semesterState = action.payload.semester || [];
      state.gradeFile = action.payload;
      state.semesterFormData.visible = false;
    });
    builder.addCase(deleteSemesterStateSemester.fulfilled, (state, action) => {
      state.semesterState = action.payload.semester || [];
      state.gradeFile = action.payload;
      state.semesterFormData.visible = false;
    });
    builder.addCase(addSemesterStateSemester.fulfilled, (state, action) => {
      state.semesterState = action.payload.semester || [];
      state.gradeFile = action.payload;
      state.semesterFormData.visible = false;
    });
  },
});

export const {
  loadSampleGradeFile,
  loadLocalGradeFile,
  setGradeTarget,
  openExcelPopup,
  showSubjectFormData,
  hideSubjectFormData,
  updateSemesterStateSubjectScreen,
  closeExcelPopup,
  showSemesterFormData,
  hideSemesterFormData,
  setGradeMapIdx,
} = gradeFileSlide.actions;
